import { User } from '@auth0/auth0-react'
import { addBusinessDays } from 'date-fns'
import React, { useCallback, useEffect } from 'react'

import { Dispensary } from '../../../../graphql/camel'
import useSiteMetadata from '../../../../hooks/useSiteMetadata'
import { UsePractitionerFormsState } from '../../../../lib/onboarding/hooks/usePractitionerFormsState'
import { setPractitionerFormsState } from '../../../../lib/onboarding/practitioner-forms-state'
import { usePageUrl } from '../../../../lib/pages'
import { usePersistentToast } from '../../../../lib/toasts/hooks/usePersistentToast'
import { getCommissionsToastActions } from '../../../onboarding/CommissionsToastActions'
import { publishEvent } from './CommissionsNotification'

interface CommissionsToastProps {
  showModal: boolean
  setShowModal: (showModal: boolean) => void
  settingsLoading: boolean
  reminderIsOverdue: boolean
  practitionerFormsAreNeeded: boolean
  excludedPathPatterns?: RegExp[]
  practitionerFormsState: UsePractitionerFormsState
  user?: User
  dispensary?: Dispensary
}

const CommissionsToast: React.FC<CommissionsToastProps> = ({
  showModal,
  setShowModal,
  settingsLoading,
  reminderIsOverdue,
  practitionerFormsAreNeeded,
  excludedPathPatterns = [],
  practitionerFormsState,
  user,
  dispensary,
}) => {
  const { navigate } = usePageUrl()
  const { commissionsNotificationToastOpensModal, commissionsNotificationDaysToHide } =
    useSiteMetadata() ?? {}
  const toastTarget = commissionsNotificationToastOpensModal ? 'modal' : 'account'
  const daysToHide = commissionsNotificationDaysToHide ?? 10

  // Initialize a toast
  const {
    shouldShowToast,
    shouldHideToast,
    showToast,
    hideToast,
    dismissToast: dismissPersistentToast,
  } = usePersistentToast({
    toastId: 'commissionsOptOut',
    condition: () => practitionerFormsAreNeeded && reminderIsOverdue && !showModal,
    excludedPathPatterns,
  })

  // Renotify after 5 days if the toast or modal is dismissed
  const dismissToast = useCallback(() => {
    dismissPersistentToast()
    publishEvent({
      eventKey: 'commissionsNotificationDismissed',
      user,
      dispensary,
    })
    setPractitionerFormsState({
      lastEvent: 'toast_dismissed',
      notifyAfter: addBusinessDays(new Date(), daysToHide).toISOString(),
    })
  }, [dismissPersistentToast, user, dispensary, daysToHide])

  const navigateToFormPageOrModal = useCallback(() => {
    // If using a toast instead of modal,
    // when the toast is clicked, depending on the toast target,
    // navigate to the form page, or open the modal
    if (toastTarget === 'account') {
      publishEvent({
        eventKey: 'commissionsNotificationNavigatedToSubmit',
        user,
        dispensary,
      })
      navigate('/account/settings/eft-and-tax')
      setPractitionerFormsState({
        lastEvent: 'toast_selected',
        notifyAfter: addBusinessDays(new Date(), daysToHide).toISOString(),
      })
    } else if (toastTarget === 'modal') {
      setShowModal(true)
    }
  }, [navigate, toastTarget, setShowModal, user, dispensary, daysToHide])

  // Show the toast
  useEffect(() => {
    if (shouldShowToast) {
      showToast('You are not yet earning commissions from your dispensary', {
        variant: 'info',
        onClick: navigateToFormPageOrModal,
        action: getCommissionsToastActions({
          dismissToast,
          navigateToDispensaryDashboard: navigateToFormPageOrModal,
        }),
      })
    }
  }, [dismissToast, navigateToFormPageOrModal, shouldShowToast, showToast])

  // Hide the toast
  useEffect(() => {
    if (shouldHideToast) {
      hideToast()
    }
  }, [shouldHideToast, hideToast])

  // cleanup
  useEffect(() => {
    if (!settingsLoading && !practitionerFormsAreNeeded && practitionerFormsState) {
      setPractitionerFormsState(undefined)
    }
  }, [practitionerFormsAreNeeded, practitionerFormsState, settingsLoading])

  return null
}

export default CommissionsToast
